import { computed } from 'vue'

/**
 * Tier color mapping - single source of truth for all tier styling.
 */
const TIER_COLORS = {
    GRAY: { text: 'slate-500', bg: 'slate-500/10' },
    '1M': { text: 'emerald-500', bg: 'emerald-500/10' },
    '1H': { text: 'sky-500', bg: 'sky-500/10' },
    '6H': { text: 'cyan-500', bg: 'cyan-500/10' },
    '12H': { text: 'indigo-500', bg: 'indigo-500/10' },
    '24H': { text: 'violet-500', bg: 'violet-500/10' },
    '3D': { text: 'amber-500', bg: 'amber-500/10' },
    '5D': { text: 'orange-500', bg: 'orange-500/10' },
    '7D': { text: 'rose-500', bg: 'rose-500/10' },
}

/**
 * Get the text color class for a tier value.
 */
export const getTierTextClass = (value) => {
    const color = TIER_COLORS[value]
    return color ? `text-${color.text}` : 'text-primary'
}

/**
 * Get the border color class for a tier value.
 */
export const getTierBorderClass = (value) => {
    const color = TIER_COLORS[value]
    return color ? `border-${color.text}/30` : 'border-primary/30'
}

/**
 * Get the background color class for a tier value.
 */
export const getTierBgClass = (value) => {
    const color = TIER_COLORS[value]
    return color ? `bg-${color.bg}` : 'bg-primary/10'
}

/**
 * Get combined text and border classes for a tier value.
 */
export const getTierColorClasses = (value) => {
    return `${getTierTextClass(value)} ${getTierBorderClass(value)}`
}

/**
 * Composable for tier calculation logic.
 *
 * @param {Ref<Array>} tiersRef - Reactive reference to the tiers array
 * @param {Ref<number>} timerRef - Reactive reference to the current timer value
 */
export function useTiers(tiersRef, timerRef) {
    const sortedTiers = computed(() =>
        [...tiersRef.value].sort((a, b) => b.seconds - a.seconds)
    )

    const currentTierValue = computed(() => {
        for (const tier of sortedTiers.value) {
            if (timerRef.value >= tier.seconds) return tier.value
        }
        return 'GRAY'
    })

    const currentTierLabel = computed(() => {
        for (const tier of sortedTiers.value) {
            if (timerRef.value >= tier.seconds) return tier.label
        }
        return '0m'
    })

    const currentTier = computed(
        () =>
            tiersRef.value.find((t) => t.value === currentTierValue.value) ||
            tiersRef.value[0]
    )

    const otherTiers = computed(() =>
        tiersRef.value.filter((t) => t.value !== currentTierValue.value)
    )

    return {
        sortedTiers,
        currentTierValue,
        currentTierLabel,
        currentTier,
        otherTiers,
    }
}
